import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { FunctionComponent, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { useNavigate } from "react-router-dom";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useUpdateListPersonCampaignMutation } from "@/pages/Private/redux/list/list.api";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { useAnchoredTooltip } from "@/utils/useAnchoredTooltip";
import { useCloseOnOutsideClick } from "@/utils/useCloseOnOutsideClick";

export const CampaignTag: FunctionComponent<{
	row: ListPerson;
	label: string;
	icon: IconDefinition;
	style: "success" | "error" | "warning" | "info";
	allowCampaignChange?: boolean;
	/**
	 * When provided (Review to send), clicking the tag triggers this instead of opening the legacy
	 * change-campaign popover — so assignment goes through the "Assign to campaign" picker.
	 */
	onAssignClick?: () => void;

	hoverLabel?: string;
	hoverIcon?: IconDefinition;
}> = ({
	label,
	icon,
	style,
	row,
	allowCampaignChange = false,
	onAssignClick,
	hoverLabel = "",
	hoverIcon,
}) => {
	const [isVisible, setIsVisible] = useState<boolean>(false);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	const [updateListPersonCampaign, { isLoading: isUpdatingListPerson }] =
		useUpdateListPersonCampaignMutation();

	const [selectedCampaign, setSelectedCampaign] = useState<{
		id: string;
		title: string;
	} | null>();

	const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (!selectedCampaign?.id) {
			return;
		}

		await updateListPersonCampaign({
			id: row.id,
			campaignId: selectedCampaign.id,
		}).unwrap();

		setIsVisible(false);
	};

	const handleEdit = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const campaignId = selectedCampaign?.id || row.campaignId;

		if (!campaignId || !row.list.campaignConfig?.[campaignId]) {
			return;
		}

		const campaign = row.list.campaignConfig[campaignId];

		const cleanParams = new URLSearchParams(window.location.search);

		cleanParams.delete("campaign");
		cleanParams.delete("campaignUuid");
		cleanParams.delete("autoDirected");
		const cleanSearch = cleanParams.toString() ? `?${cleanParams.toString()}` : "";

		sessionStorage.setItem("campaignEditReturnTo", window.location.pathname + cleanSearch);

		navigate(
			`/app/lists/create/copywriting/${row.list.id}?campaign=${encodeURIComponent(campaign?.name)}&campaignUuid=${campaignId}&autoDirected=true`
		);
	};

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useAnchoredTooltip(triggerRef, tooltipRef, isVisible);

	const campaignsInList = useMemo(() => {
		return Object.entries(row?.list?.campaignConfig || {})
			.filter(([, config]) => (config as { isValid?: boolean })?.isValid === true)
			.map(([campaign, config]) => ({
				id: campaign,
				title: (config as { name?: string })?.name ?? "",
			}));
	}, [row]);

	const navigate = useNavigate();

	const renderChangeResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className="fixed flex flex-col z-100 p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[300px] max-w-[300px]"
			>
				<div className="flex flex-col gap-2">
					<div>
						<Dropdown
							resultText={"noActiveCampaigns"}
							data={campaignsInList}
							defaultValue={
								row.campaignId
									? {
											id: row.campaignId,
											title: row?.list?.campaignConfig?.[row.campaignId]?.name,
										}
									: undefined
							}
							floating={true}
							portal={true}
							handleSelect={(selected) => {
								setSelectedCampaign({
									id: selected.id as string,
									title: selected.title,
								});
							}}
							label="Campaign"
							size={ButtonSize.S}
						/>
					</div>
				</div>
				<div className="flex gap-2 flex-col">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={!(selectedCampaign?.id || row.campaignId)}
						isLoading={isUpdatingListPerson}
						title="Edit campaign"
						type="button"
						onClick={handleEdit}
					/>
					<div className="flex flex-row gap-2 justify-end">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title="Close"
							type="button"
							onClick={() => setIsVisible(false)}
						/>
						<Button
							color={ButtonColor.ACTION}
							disabled={!selectedCampaign?.id}
							isLoading={isUpdatingListPerson}
							title="Save"
							onClick={handleSave}
						/>
					</div>
				</div>
			</div>,
			document.body
		);
	};

	const containerStyles = {
		success:
			"border-status-success/50 bg-status-success-light hover:bg-status-success hover:text-status-success-light",
		error: "border-status-error/50 bg-status-error-light hover:bg-status-error hover:text-white",
		warning:
			"border-status-warning/50 bg-status-warning-light hover:bg-status-warning hover:text-white",
		info: "border-status-info/50 bg-status-info-light hover:bg-status-info hover:text-white",
	};

	const iconStyles = {
		success:
			"text-status-success bg-status-success/20 group-hover:text-status-success-light group-hover:bg-status-success-light/30",
		error:
			"text-status-error bg-status-error/20 group-hover:text-status-error-light group-hover:bg-status-error-light/30",
		warning:
			"text-status-warning bg-status-warning/20 group-hover:text-status-warning-light group-hover:bg-status-warning-light/30",
		info: "text-status-info bg-status-info/20 group-hover:text-status-info-light group-hover:bg-status-info-light/30",
	};

	const textStyles = {
		success: "text-status-success group-hover:text-status-success-light",
		error: "text-status-error group-hover:text-status-error-light",
		warning: "text-status-warning group-hover:text-status-warning-light",
		info: "text-status-info group-hover:text-status-info-light",
	};

	const hasHover = !!(hoverIcon && hoverLabel);

	return (
		<span
			className={classNames(
				"mr-1 mb-1 px-2 overflow-hidden group whitespace-nowrap py-1 border rounded-md text-sm text-left inline-flex flex-row gap-1  items-center transition-colors cursor-pointer max-w-[300px]",
				containerStyles[style]
			)}
		>
			{isVisible && renderChangeResult()}
			<Tooltip content={label}>
				<div
					ref={triggerRef}
					className="flex flex-row gap-1 items-center min-w-0"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();

						if (onAssignClick) {
							onAssignClick();

							return;
						}

						if (allowCampaignChange) {
							setIsVisible?.(true);
						}
					}}
				>
					{hasHover ? (
						<div className="relative inline-flex items-center min-w-0 overflow-hidden">
							<span className="invisible inline-flex items-center gap-1 min-w-0">
								<Icon className="rounded-full p-[3px] w-4 h-4 mr-1 shrink-0" icon={icon} />
								<span className="truncate">{label}</span>
							</span>

							<span className="absolute inset-0 inline-flex items-center gap-1 opacity-100 group-hover:opacity-0 transition-opacity min-w-0">
								<Icon
									className={classNames(
										"rounded-full p-[3px] w-4 h-4 mr-1 shrink-0",
										iconStyles[style]
									)}
									icon={icon}
								/>
								<span className={`${textStyles[style]} truncate`}>{label}</span>
							</span>

							<span className="absolute inset-0 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity min-w-0">
								<Icon
									className={classNames(
										"rounded-full p-[3px] w-4 h-4 mr-1 shrink-0",
										iconStyles[style]
									)}
									icon={hoverIcon}
								/>
								<span className="truncate">{hoverLabel}</span>
							</span>
						</div>
					) : (
						<div className="inline-flex items-center gap-1 min-w-0">
							<Icon
								className={classNames(
									"rounded-full p-[3px] w-4 h-4 mr-1 shrink-0",
									iconStyles[style]
								)}
								icon={icon}
							/>
							<span className={`${textStyles[style]} truncate`}>{label}</span>
						</div>
					)}
				</div>
			</Tooltip>
		</span>
	);
};
